import useAlertStore, { AlertOptions, AlertType, ShowAlertInput } from "./useAlertStore";
import {
  BackendErrorDetails,
  hasApiErrorAlertBeenShown,
  markApiErrorAlertShown,
  normalizeApiError,
  NormalizedApiError,
} from "../util/apiError";

type LegacyAlertType = AlertType | "warn";

const mapAlertType = (type?: LegacyAlertType): AlertType => {
  if (type === "warn") return "warning";
  return type || "info";
};

export const alertService = {
  show: (input: ShowAlertInput) => useAlertStore.getState().show(input),

  showAlert: (
    message: string,
    type: LegacyAlertType = "info",
    details?: BackendErrorDetails,
    options?: Omit<AlertOptions, "details">
  ) =>
    useAlertStore.getState().show({
      ...options,
      message,
      type: mapAlertType(type),
      details,
    }),

  success: (
    message: string,
    details?: BackendErrorDetails,
    options?: Omit<AlertOptions, "details">
  ) => useAlertStore.getState().success(message, details, options),

  error: (
    message: string,
    details?: BackendErrorDetails,
    options?: Omit<AlertOptions, "details">
  ) => useAlertStore.getState().error(message, details, options),

  warning: (
    message: string,
    details?: BackendErrorDetails,
    options?: Omit<AlertOptions, "details">
  ) => useAlertStore.getState().warning(message, details, options),

  info: (
    message: string,
    details?: BackendErrorDetails,
    options?: Omit<AlertOptions, "details">
  ) => useAlertStore.getState().info(message, details, options),

  showApiError: (
    error: unknown,
    fallbackMessage?: string,
    options?: Omit<AlertOptions, "details" | "title" | "dedupeKey"> & { force?: boolean }
  ): NormalizedApiError => {
    const apiError = normalizeApiError(error, fallbackMessage);

    if (!hasApiErrorAlertBeenShown(error) || options?.force) {
      useAlertStore.getState().show({
        ...options,
        type: "error",
        title: apiError.title,
        message: apiError.message,
        details: apiError.details,
        dedupeKey: apiError.dedupeKey,
      });

      markApiErrorAlertShown(error);
    }

    return apiError;
  },

  clear: () => useAlertStore.getState().clearAlerts(),
};

export default alertService;
